import { useTranslation } from 'react-i18next'
import moment from 'moment/moment'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import {
  GENERATOR_INPUT_TYPE,
  TIME_LINE_MODE,
  TIME_LINE_MODE_AUTOCOMPLETE_OPTIONS,
} from '@constants'
import { AutocompleteOption } from '@types'

import { useHandlers } from './useHandlers'

type UseGeneralInputsType = ReturnType<typeof useHandlers>

export const useGeneralInputs = (props: UseGeneralInputsType) => {
  const { data, state, handlers } = props

  const { t } = useTranslation()

  const { watchGeneralTimeIntervalRange, watchMode, isTimeLineLoading } = state
  const { searchAssistantsOptions } = data
  const { handleChangeGeneralMode } = handlers

  const isDateTimeMode = watchMode?.id === TIME_LINE_MODE.DATE_TIME

  const timeIntervalInputs: FormInputsType[] = [
    {
      name: 'timeInterval',
      inputs: [
        {
          dateTimePickerViews: ['day'],
          name: 'timeInterval.dateRange',
          formatDate: 'DD.MM.YYYY',
          label: t('timeLinesCreateOrEdit.general.dateRange'),
          inputType: GENERATOR_INPUT_TYPE.DATE_RANGE_PICKER,
          value: watchGeneralTimeIntervalRange,
          rangeDatePickerProps: {
            start: {
              maxDate: watchGeneralTimeIntervalRange?.length
                ? moment(watchGeneralTimeIntervalRange[1])
                : undefined,
            },
            end: {
              minDate: watchGeneralTimeIntervalRange?.length
                ? moment(watchGeneralTimeIntervalRange[0])
                : undefined,
            },
          },
        },
        {
          name: 'timeInterval.showFilter',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('timeLinesCreateOrEdit.general.showTimeIntervalFilter'),
          labelPlacement: 'end',
          formInputContainerSx: {
            display: 'flex',
          },
        },
      ],
    },
  ]

  const generalDataInputs: FormInputsType[] = isTimeLineLoading
    ? []
    : [
        {
          name: 'mode',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          autocompleteOptions: TIME_LINE_MODE_AUTOCOMPLETE_OPTIONS,
          label: t('timeLinesCreateOrEdit.general.mode'),
          placeholder: t('timeLinesCreateOrEdit.general.selectMode'),
          disableClearable: true,
          onChangeAutocomplete: value => {
            handleChangeGeneralMode(value as AutocompleteOption<TIME_LINE_MODE>)
          },
        },
        ...(isDateTimeMode ? timeIntervalInputs : []),
        {
          name: 'checkboxes',
          inputs: [
            {
              name: 'showUpdateTimeLineDataButton',
              inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
              label: t('timeLinesCreateOrEdit.general.showUpdateButton'),
              labelPlacement: 'end',
              labelSx: {
                width: 150,
              },
            },
            ...(isDateTimeMode
              ? [
                  {
                    name: 'showDayOfWeek',
                    inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                    label: t('timeLinesCreateOrEdit.general.showDayOfWeek'),
                    labelPlacement: 'end' as const,
                    labelSx: {
                      width: 150,
                    },
                  },
                ]
              : []),
          ],
        },
      ]

  const generalDataSearchInputs: FormInputsType[] = isTimeLineLoading
    ? []
    : [
        {
          name: 'searchAssistant',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          autocompleteOptions: searchAssistantsOptions,
          label: t('timeLinesCreateOrEdit.general.searchAssistant'),
          placeholder: t('timeLinesCreateOrEdit.general.selectSearchAssistant'),
          labelSx: { minWidth: 150 },
        },
        {
          name: 'filterObjectYByObjectX',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('timeLinesCreateOrEdit.general.filterObjectYByObjectX'),
          labelSx: { minWidth: 250 },
        },
        ...(!isDateTimeMode
          ? [
              {
                name: 'filterColumnsObjectByObjectX',
                inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                label: t('timeLinesCreateOrEdit.general.filterColumnsObjectByObjectX'),
                labelSx: { minWidth: 250 },
              },
            ]
          : []),
      ]

  return {
    generalDataInputs,
    generalDataSearchInputs,
  }
}
